import * as React from 'react';
import * as ImageCropper from 'react-image-crop';
import * as utils from 'utils';

import 'react-image-crop/dist/ReactCrop.css';

interface Properties {
  source: string | File | Blob | null;
  crop?: ImageCropper.Crop;
  onChange: (data: Blob | null, crop: ImageCropper.Crop) => any;
}

interface State {
  imageReference: HTMLImageElement | null;
  dataUri: string;
  crop: ImageCropper.Crop;
}

export class Cropper extends React.Component<Properties> {
  state: State = {
    imageReference: null,
    dataUri: '',
    crop: this.props.crop || {
      aspect: 1,
      x: 0,
      y: 0,
      width: 100,
      height: 100,
    },
  };

  async componentDidMount() {
    await this.prepareDataUri(this.props.source);
  }

  async componentWillReceiveProps(next: Readonly<Properties>) {
    if (next.source !== this.props.source) {
      await this.prepareDataUri(next.source);
    }
  }

  makeFirstCrop = () => {
    const downEvent = new MouseEvent('mousedown', {
      bubbles: true,
      cancelable: true,
      view: window,
    });

    const upEvent = new MouseEvent('mouseup', {
      bubbles: true,
      cancelable: true,
      view: window,
    });

    const selector = document.querySelector('.ReactCrop__crop-selection');

    if (selector) {
      selector.dispatchEvent(downEvent);
      selector.dispatchEvent(upEvent);
    }
  }

  prepareDataUri = async (source: string | File | Blob | null) => {
    if (source && (source.constructor.name === 'File')) {
      const dataUri = await utils.getFilePreview(source as File | Blob);

      this.setState(
        { dataUri: dataUri.preview },
        () => {
          setTimeout(this.makeFirstCrop, 1000);
        },
      );
    }
  }

  onImageLoaded = (imageReference: HTMLImageElement) => {
    this.setState({ imageReference });
  }

  onCropChanged = (data: ImageCropper.Crop) => {
    this.setState({ crop: data });
  }

  onCropCompleted = async (localCrop: ImageCropper.Crop, crop: ImageCropper.PixelCrop) => {
    if (this.state.imageReference) {
      const modifiedBlob = await this.createBlobFromCrop(this.state.imageReference, crop);
      this.props.onChange(modifiedBlob, localCrop);

      this.setState({
        width: crop.width,
        height: crop.height,
      });
    }
  }

  createBlobFromCrop = (image: HTMLImageElement, crop: ImageCropper.Crop): Promise<Blob | null> => {
    if (!image) {
      return Promise.resolve(null);
    }

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    canvas.width = crop.width || 0;
    canvas.height = crop.height || 0;

    if (!!context) {
      context.drawImage(
        image,
        crop.x,
        crop.y,
        canvas.width,
        canvas.height,
        0,
        0,
        canvas.width,
        canvas.height,
      );
    }

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob: Blob | null) => {
          resolve(blob);
        },
        'image/png');
    });
  }

  render() {
    // tslint:disable-next-line
    const Component = (ImageCropper as any).default;
    const { source } = this.props;
    const { crop, dataUri } = this.state;

    return (<Component
      crossorigin="*"
      crop={crop}
      src={typeof source === 'string' ? source : dataUri}
      onChange={this.onCropChanged}
      onComplete={this.onCropCompleted}
      onImageLoaded={this.onImageLoaded}
    />);
  }
}
